import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDocs, runTransaction } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useWindowSize } from '@custom-react-hooks/all';
import styles from '../WheelOfFortune.module.css';
import AlyfLogo from '../assets/Alyf.svg';
import WinningPopup from './WiningPopup';
import Loader from './Loader';
import {
  DataContext,
  Prize,
  PrizeDisplayMode,
  PrizeLabelTextColor,
  PrizeResultType,
} from '../context/DataContext';
import { useUser } from '../context/UserProvider';
import { db } from './firebase-config';
import { useAppConfig } from '../context/AppConfigContext';
import { WHEEL_CONFIG, APP_DEFAULTS } from '../config';
import {
  hasReachedMaxSpins,
  incrementSpinCount,
  isCampaignEnded,
} from '../utils/campaign';

const TAU = 2 * Math.PI;
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const DEFAULT_SECTOR_COLOR = '#37355D';
const PRE_SPIN_ANGULAR_SPEED = TAU * 0.28;
const EXTRA_SETTLE_SPINS = 5;
const VALID_DISPLAY_MODES: PrizeDisplayMode[] = [
  'logo-label',
  'label-only',
  'logo-only',
  'logo-background',
  'logo-background-only',
];

const normalizeAngle = (angle: number) => ((angle % TAU) + TAU) % TAU;

const easeOutCubic = (value: number) => 1 - Math.pow(1 - value, 3);
const normalizeColor = (value: string | undefined) => {
  const color = value?.trim();
  if (!color) {
    return '';
  }

  if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
    return CSS.supports('color', color) ? color : '';
  }

  return color;
};

const WheelOfFortune: React.FC = () => {
  const navigate = useNavigate();
  const { data, setData } = useContext(DataContext);
  const { user, clearUser } = useUser();
  const { config } = useAppConfig();
  const { width } = useWindowSize();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const spinRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const angRef = useRef<number>(0);
  const pendingSpinTimestampRef = useRef<number>(0);
  const logoCacheRef = useRef<Map<string, HTMLImageElement | null>>(new Map());

  const [loadingPrizes, setLoadingPrizes] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [firstSpin, setFirstSpin] = useState(true);
  const [currentLabel, setCurrentLabel] = useState(
    config.spinPromptLabel || APP_DEFAULTS.spinPromptLabel
  );
  const [currentSector, setCurrentSector] = useState<Prize | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);

  const canvasSize = Math.min(
    WHEEL_CONFIG.canvasSize,
    width > 700 ? WHEEL_CONFIG.canvasSize : Math.max(width - 40, 280)
  );

  const getPrizeResultType = useCallback((prize: Prize): PrizeResultType => {
    const resultType = prize.resultType?.trim().toLowerCase();
    return resultType === 'fail' ? 'fail' : 'win';
  }, []);

  const getPrizeOrder = useCallback((prize: Prize) => {
    const rawOrder =
      typeof prize.order === 'string' ? Number.parseFloat(prize.order) : prize.order;
    return Number.isFinite(rawOrder) ? (rawOrder as number) : Number.MAX_SAFE_INTEGER;
  }, []);

  const sectors = useMemo(
    () =>
      data
        .filter((item) => item.Qte > 0)
        .slice()
        .sort((left, right) => {
          const orderDifference = getPrizeOrder(left) - getPrizeOrder(right);
          if (orderDifference !== 0) {
            return orderDifference;
          }

          return left.label.localeCompare(right.label);
        }),
    [data, getPrizeOrder]
  );
  const total = sectors.length;
  const arc = total > 0 ? TAU / total : TAU;
  const campaignIsEnded = isCampaignEnded(config.campaignEndsAt);
  const spinLimitReached = hasReachedMaxSpins(user.phone, config.maxSpinsPerUser);
  const spinBlocked = spinning || !config.campaignEnabled || campaignIsEnded || spinLimitReached;

  const blockedMessage = useMemo(() => {
    if (campaignIsEnded) {
      return 'This campaign has already ended.';
    }

    if (!config.campaignEnabled) {
      return 'This campaign is currently disabled.';
    }

    if (!spinning && spinLimitReached) {
      return 'You have already reached the allowed number of spins.';
    }

    return null;
  }, [campaignIsEnded, config.campaignEnabled, spinLimitReached, spinning]);

  const getPrizeLogoUrl = useCallback(
    (prize: Prize) => prize.logoUrl?.trim() || prize.logo?.trim() || '',
    []
  );

  const getPrizeDisplayMode = useCallback((prize: Prize): PrizeDisplayMode => {
    const mode = prize.displayMode?.trim().toLowerCase() as PrizeDisplayMode | undefined;
    return mode && VALID_DISPLAY_MODES.includes(mode) ? mode : 'logo-label';
  }, []);

  const getPrizeWheelLogoScale = useCallback((prize: Prize, fallback = 1) => {
    const rawScale =
      typeof prize.wheelLogoScale === 'string'
        ? Number.parseFloat(prize.wheelLogoScale)
        : prize.wheelLogoScale;

    return Number.isFinite(rawScale) ? clamp(rawScale as number, 0.4, 2.4) : fallback;
  }, []);

  const getPrizeLabelTextColor = useCallback((prize: Prize): PrizeLabelTextColor => {
    const color = prize.labelTextColor?.trim().toLowerCase();
    return color === 'black' ? 'black' : 'white';
  }, []);

  const getPrizeBaseColor = useCallback(
    (prize: Prize) => normalizeColor(prize.color) || DEFAULT_SECTOR_COLOR,
    []
  );

  const getPrizeBackgroundColors = useCallback(
    (prize: Prize) => ({
      start: normalizeColor(prize.wheelBackgroundColor),
      end: normalizeColor(prize.wheelBackgroundColor2),
    }),
    []
  );

  const refreshPrizes = useCallback(async () => {
    setLoadingPrizes(true);
    try {
      const querySnapshot = await getDocs(collection(db, APP_DEFAULTS.firestoreCollection));
      const dataList = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      })) as Prize[];
      setData(dataList);
    } catch (error) {
      console.error('Failed to load prizes for wheel:', error);
    } finally {
      setLoadingPrizes(false);
    }
  }, [setData]);

  const getIndexForAngle = useCallback(
    (angle: number) => {
      if (!total) {
        return -1;
      }

      const normalizedAngle = normalizeAngle(angle);
      return Math.floor(total - (normalizedAngle / TAU) * total) % total;
    },
    [total]
  );

  const getTargetAngleForIndex = useCallback(
    (index: number) => ((total - index - 0.5) / total) * TAU,
    [total]
  );

  const getWrappedLines = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      label: string,
      maxWidth: number,
      maxLines: number
    ) => {
      const words = (label ?? '').trim().split(/\s+/).filter(Boolean);
      if (words.length === 0) {
        return [''];
      }

      const lines: string[] = [];
      let currentLine = words[0];

      for (let index = 1; index < words.length; index += 1) {
        const nextLine = `${currentLine} ${words[index]}`;
        if (ctx.measureText(nextLine).width <= maxWidth || currentLine.length === 0) {
          currentLine = nextLine;
          continue;
        }

        lines.push(currentLine);
        currentLine = words[index];

        if (lines.length === maxLines - 1) {
          break;
        }
      }

      const consumedWords = lines.join(' ').split(/\s+/).filter(Boolean).length;
      const remainingWords = words.slice(consumedWords);
      const lastLine = remainingWords.join(' ') || currentLine;

      if (lines.length < maxLines) {
        lines.push(lastLine);
      }

      if (lines.length > maxLines) {
        return lines.slice(0, maxLines);
      }

      return lines.map((line, index) => {
        if (index !== lines.length - 1) {
          return line;
        }

        if (ctx.measureText(line).width <= maxWidth) {
          return line;
        }

        let truncated = line;
        while (truncated.length > 3 && ctx.measureText(`${truncated}...`).width > maxWidth) {
          truncated = truncated.slice(0, -1).trimEnd();
        }

        return `${truncated}...`;
      });
    },
    []
  );

  const drawContainedImage = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      image: HTMLImageElement,
      centerX: number,
      centerY: number,
      maxWidth: number,
      maxHeight: number
    ) => {
      const sourceWidth = image.naturalWidth || image.width;
      const sourceHeight = image.naturalHeight || image.height;

      if (!sourceWidth || !sourceHeight) {
        return;
      }

      const scale = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight);
      const drawWidth = sourceWidth * scale;
      const drawHeight = sourceHeight * scale;

      ctx.drawImage(
        image,
        centerX - drawWidth / 2,
        centerY - drawHeight / 2,
        drawWidth,
        drawHeight
      );
    },
    []
  );

  const drawContainedImageInSegment = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      image: HTMLImageElement,
      radius: number,
      sectorArc: number,
      mode: PrizeDisplayMode,
      scale: number
    ) => {
      const sourceWidth = image.naturalWidth || image.width;
      const sourceHeight = image.naturalHeight || image.height;
      const tangentHalfArc = Math.tan(sectorArc / 2);

      if (!sourceWidth || !sourceHeight || tangentHalfArc <= 0) {
        return;
      }

      const aspectRatio = sourceWidth / sourceHeight;
      const outerRadius = radius * 0.94;
      const centerX = radius * (mode === 'logo-background-only' ? 0.58 : 0.56);

      const maxHeightFromAngle =
        (2 * centerX * tangentHalfArc) / (1 + aspectRatio * tangentHalfArc);

      const quadraticA = (aspectRatio * aspectRatio + 1) / 4;
      const quadraticB = centerX * aspectRatio;
      const quadraticC = centerX * centerX - outerRadius * outerRadius;
      const discriminant = quadraticB * quadraticB - 4 * quadraticA * quadraticC;

      if (discriminant <= 0) {
        return;
      }

      const maxHeightFromRadius =
        (-quadraticB + Math.sqrt(discriminant)) / (2 * quadraticA);

      const maxHeight = Math.max(
        0,
        Math.min(maxHeightFromAngle, maxHeightFromRadius)
      );

      if (!maxHeight) {
        return;
      }

      const occupancy = clamp(scale, 0.3, 1.5);

      const maxWidth = maxHeight * aspectRatio;

      drawContainedImage(
        ctx,
        image,
        centerX,
        0,
        maxWidth * occupancy,
        maxHeight * occupancy
      );
    },
    [drawContainedImage]
  );

  const drawSector = useCallback(
    (ctx: CanvasRenderingContext2D, sector: Prize, index: number) => {
      const angle = arc * index;
      const radius = canvasSize / 2;
      const logoUrl = getPrizeLogoUrl(sector);
      const logo = logoUrl ? logoCacheRef.current.get(logoUrl) ?? null : null;
      const displayMode = getPrizeDisplayMode(sector);
      const hasLogo = Boolean(logo);
      const usesLogoBackground =
        (displayMode === 'logo-background' || displayMode === 'logo-background-only') && hasLogo;
      const showLogoOnly = displayMode === 'logo-only' && hasLogo;
      const showLogoWithLabel = displayMode === 'logo-label' && hasLogo;
      const showLabel =
        displayMode === 'label-only' ||
        displayMode === 'logo-label' ||
        displayMode === 'logo-background' ||
        !hasLogo;
      const baseColor = getPrizeBaseColor(sector);
      const backgroundColors = getPrizeBackgroundColors(sector);
      const usesManualFlatColor = Boolean(backgroundColors.start) && !backgroundColors.end;
      const labelTextColor = getPrizeLabelTextColor(sector);
      const wheelLogoScale = getPrizeWheelLogoScale(
        sector,
        usesLogoBackground ? 1 : 1
      );
      const fontSize = clamp(Math.round(canvasSize / 22), 14, 28);
      const lineHeight = fontSize * 1.05;
      const stackedLogoSize = showLogoWithLabel
        ? clamp(Math.round((canvasSize / 12) * wheelLogoScale), 24, 96)
        : 0;
      const maxTextWidth = radius * 0.48;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(radius, radius);
      ctx.arc(radius, radius, radius, angle, angle + arc);
      ctx.lineTo(radius, radius);
      ctx.closePath();

      if (backgroundColors.start || backgroundColors.end) {
        if (usesManualFlatColor) {
          ctx.fillStyle = backgroundColors.start;
        } else {
          const gradient = ctx.createLinearGradient(0, 0, canvasSize, canvasSize);
          gradient.addColorStop(0, backgroundColors.start || baseColor);
          gradient.addColorStop(1, backgroundColors.end || backgroundColors.start || baseColor);
          ctx.fillStyle = gradient;
        }
        ctx.fill();
      } else {
        ctx.fillStyle = baseColor;
        ctx.fill();
      }

      ctx.lineWidth = clamp(canvasSize * 0.008, 2, 6);
      ctx.strokeStyle = 'rgba(243, 187, 26, 0.9)';
      ctx.stroke();

      ctx.translate(radius, radius);
      ctx.rotate(angle + arc / 2);
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = labelTextColor === 'black' ? '#000000' : WHEEL_CONFIG.labelColor;
      ctx.font = `bold ${fontSize}px sans-serif`;

      if (usesLogoBackground && logo) {
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.arc(0, 0, radius, -arc / 2, arc / 2);
        ctx.closePath();
        ctx.clip();
        drawContainedImageInSegment(
          ctx,
          logo,
          radius,
          arc,
          displayMode,
          wheelLogoScale
        );
        if (!usesManualFlatColor) {
          const overlay = ctx.createLinearGradient(0, -radius, radius, radius);
          overlay.addColorStop(0, 'rgba(255, 255, 255, 0.12)');
          overlay.addColorStop(1, 'rgba(255, 255, 255, 0.02)');
          ctx.fillStyle = overlay;
          ctx.fillRect(0, -radius, radius, radius * 2);
        }
        ctx.restore();
      }

      if (showLogoOnly && logo) {
        const logoSize = clamp(Math.round((canvasSize / 7.5) * wheelLogoScale), 52, 180);
        const logoCenterX = radius * 0.58;
        ctx.save();
        ctx.beginPath();
        ctx.arc(logoCenterX, 0, logoSize / 2, 0, TAU);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(logo, logoCenterX - logoSize / 2, -logoSize / 2, logoSize, logoSize);
        ctx.restore();
        ctx.restore();
        return;
      }

      const lines = showLabel ? getWrappedLines(ctx, sector.label, maxTextWidth, 2) : [];
      const contentX = radius - Math.max(18, canvasSize * 0.055);
      const gap = showLogoWithLabel ? 8 : 0;
      const totalContentHeight = lines.length * lineHeight + stackedLogoSize + gap;
      let cursorY = -totalContentHeight / 2;

      if (showLogoWithLabel && logo) {
        const logoX = contentX - stackedLogoSize;
        ctx.save();
        ctx.beginPath();
        ctx.arc(
          logoX + stackedLogoSize / 2,
          cursorY + stackedLogoSize / 2,
          stackedLogoSize / 2,
          0,
          TAU
        );
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(logo, logoX, cursorY, stackedLogoSize, stackedLogoSize);
        ctx.restore();
        cursorY += stackedLogoSize + gap;
      }

      lines.forEach((line) => {
        if (usesLogoBackground) {
          ctx.lineJoin = 'round';
          ctx.lineWidth = Math.max(3, fontSize * 0.18);
          ctx.strokeStyle =
            labelTextColor === 'black'
              ? 'rgba(255, 255, 255, 0.75)'
              : 'rgba(0, 0, 0, 0.45)';
          ctx.strokeText(line, contentX, cursorY + lineHeight / 2, maxTextWidth);
        }
        ctx.fillText(line, contentX, cursorY + lineHeight / 2, maxTextWidth);
        cursorY += lineHeight;
      });
      ctx.restore();
    },
    [
      arc,
      canvasSize,
      drawContainedImageInSegment,
      getPrizeBaseColor,
      getPrizeBackgroundColors,
      getPrizeDisplayMode,
      getPrizeLabelTextColor,
      getPrizeLogoUrl,
      getPrizeWheelLogoScale,
      getWrappedLines,
    ]
  );

  const rotateTo = useCallback(
    (angle: number) => {
      if (!total || !canvasRef.current) {
        return;
      }

      angRef.current = angle;
      canvasRef.current.style.transform = `rotate(${angle - Math.PI / 2}rad)`;

      const sectorIndex = getIndexForAngle(angle);
      const sector = sectors[sectorIndex];
      if (!sector || !spinRef.current) {
        return;
      }

      spinRef.current.style.background = '#37355D';
    },
    [getIndexForAngle, sectors, total]
  );

  useEffect(() => {
    if (data.length > 0) {
      return;
    }

    refreshPrizes();
  }, [data.length, refreshPrizes]);

  useEffect(() => {
    let cancelled = false;

    sectors.forEach((sector) => {
      const logoUrl = getPrizeLogoUrl(sector);
      if (!logoUrl || logoCacheRef.current.has(logoUrl)) {
        return;
      }

      const image = new Image();
      image.onload = () => {
        if (cancelled) {
          return;
        }

        logoCacheRef.current.set(logoUrl, image);
        setLogoVersion((value) => value + 1);
      };
      image.onerror = () => {
        if (cancelled) {
          return;
        }

        logoCacheRef.current.set(logoUrl, null);
        setLogoVersion((value) => value + 1);
      };
      image.src = logoUrl;
    });

    return () => {
      cancelled = true;
    };
  }, [getPrizeLogoUrl, sectors]);

  useEffect(() => {
    if (!canvasRef.current || total === 0) {
      return;
    }

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.clearRect(0, 0, canvasSize, canvasSize);
    sectors.forEach((sector, index) => drawSector(ctx, sector, index));
    ctx.save();
    ctx.beginPath();
    ctx.arc(canvasSize / 2, canvasSize / 2, canvasSize / 2 - clamp(canvasSize * 0.008, 2, 6) / 2, 0, TAU);
    ctx.lineWidth = clamp(canvasSize * 0.015, 6, 10);
    ctx.strokeStyle = 'rgba(255, 215, 0, 0.95)';
    ctx.stroke();
    ctx.restore();
    rotateTo(angRef.current);
  }, [canvasSize, drawSector, logoVersion, rotateTo, sectors, total]);

  useEffect(() => {
    if (blockedMessage) {
      setCurrentLabel(blockedMessage);
      return;
    }

    setCurrentLabel(config.spinPromptLabel || APP_DEFAULTS.spinPromptLabel);
  }, [blockedMessage, config.spinPromptLabel]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const startPendingSpin = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    pendingSpinTimestampRef.current = 0;

    const step = (timestamp: number) => {
      if (!pendingSpinTimestampRef.current) {
        pendingSpinTimestampRef.current = timestamp;
      }

      const deltaSeconds = (timestamp - pendingSpinTimestampRef.current) / 1000;
      pendingSpinTimestampRef.current = timestamp;
      rotateTo(angRef.current + deltaSeconds * PRE_SPIN_ANGULAR_SPEED);
      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
  }, [rotateTo]);

  const animateToAngle = useCallback(
    (targetAngle: number, winningPrize: Prize) => {
      cancelAnimationFrame(rafRef.current);
      pendingSpinTimestampRef.current = 0;

      const startAngle = angRef.current;
      const delta = targetAngle - startAngle;
      const startTime = performance.now();

      const step = (now: number) => {
        const progress = Math.min((now - startTime) / WHEEL_CONFIG.spinDurationMs, 1);
        const easedProgress = easeOutCubic(progress);
        rotateTo(startAngle + delta * easedProgress);

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(step);
          return;
        }

        setData((prev) =>
          prev.map((item) => (item.id === winningPrize.id ? winningPrize : item))
        );
        setSpinning(false);
        setCurrentSector(winningPrize);
        setCurrentLabel(winningPrize.label);
        setModalVisible(true);
      };

      rafRef.current = requestAnimationFrame(step);
    },
    [rotateTo, setData]
  );

  const reservePrize = useCallback(async (prize: Prize) => {
    const prizeRef = doc(db, APP_DEFAULTS.firestoreCollection, prize.id);

    return runTransaction(db, async (transaction) => {
      const prizeSnap = await transaction.get(prizeRef);
      if (!prizeSnap.exists()) {
        throw new Error('Prize not found.');
      }

      const latestPrize = prizeSnap.data() as Prize;
      if (latestPrize.Qte <= 0) {
        throw new Error('Prize is no longer available.');
      }

      transaction.update(prizeRef, { Qte: latestPrize.Qte - 1 });
      return {
        ...prize,
        Qte: latestPrize.Qte - 1,
      };
    });
  }, []);

  const handleSpin = useCallback(async () => {
    if (spinning || total === 0) {
      return;
    }

    if (!user.phone) {
      navigate('/contact');
      return;
    }

    if (blockedMessage) {
      setCurrentLabel(blockedMessage);
      return;
    }

    try {
      setSpinning(true);
      setFirstSpin(false);
      setModalVisible(false);
      setCurrentLabel('Preparing your spin...');
      startPendingSpin();

      const winnerIndex = Math.floor(Math.random() * sectors.length);
      const selectedPrize = sectors[winnerIndex];
      const reserveStartTime = performance.now();
      const reservedPrize = await reservePrize(selectedPrize);
      console.info(
        `[Wheel] Prize reservation completed in ${Math.round(
          performance.now() - reserveStartTime
        )}ms`
      );
      incrementSpinCount(user.phone);

      const currentNormalized = normalizeAngle(angRef.current);
      let targetNormalized = normalizeAngle(getTargetAngleForIndex(winnerIndex));

      while (targetNormalized <= currentNormalized) {
        targetNormalized += TAU;
      }

      const targetAngle =
        angRef.current + (targetNormalized - currentNormalized) + TAU * EXTRA_SETTLE_SPINS;

      animateToAngle(targetAngle, reservedPrize);
    } catch (error) {
      cancelAnimationFrame(rafRef.current);
      pendingSpinTimestampRef.current = 0;
      console.error('Spin failed:', error);
      setSpinning(false);
      setCurrentLabel(config.spinPromptLabel || APP_DEFAULTS.spinPromptLabel);
      await refreshPrizes();
    }
  }, [
    animateToAngle,
    blockedMessage,
    config.spinPromptLabel,
    getTargetAngleForIndex,
    navigate,
    refreshPrizes,
    reservePrize,
    sectors,
    spinning,
    startPendingSpin,
    total,
    user.phone,
  ]);

  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    clearUser();
    navigate('/');
  }, [clearUser, navigate]);

  if (loadingPrizes) {
    return <Loader show={true} />;
  }

  if (sectors.length === 0) {
    return (
      <div className={styles.container} style={{ color: 'white', fontSize: 24 }}>
        No prizes are available at the moment.
      </div>
    );
  }

  return (
    <div>
      <div id={styles.container}>
        <div id={styles.wheelOfFortune}>
          <canvas ref={canvasRef} id="wheel" width={canvasSize} height={canvasSize} />
          <div
            ref={spinRef}
            id={styles.spin}
            onClick={spinBlocked ? undefined : handleSpin}
            role="button"
            aria-label="Spin the wheel"
            aria-disabled={spinBlocked}
            style={
              spinBlocked
                ? { opacity: 0.7, cursor: 'not-allowed', background: '#37355D' }
                : { background: '#37355D' }
            }
          >
            <img
              src={AlyfLogo}
              alt={config.brandName || APP_DEFAULTS.brandName}
              style={{ width: '72%', height: '72%', objectFit: 'contain' }}
            />
          </div>
        </div>
        <div className={styles.textarea}>
          {firstSpin ? config.spinPromptLabel || APP_DEFAULTS.spinPromptLabel : currentLabel}
        </div>
        {blockedMessage && (
          <p className="mt-4 text-sm text-red-300 text-center">{blockedMessage}</p>
        )}
        {!firstSpin && (
          <WinningPopup
            isVisible={modalVisible}
            onClose={handleCloseModal}
            winningItem={currentSector?.label || currentLabel}
            winningLogoUrl={
              currentSector && getPrizeDisplayMode(currentSector) !== 'label-only'
                ? getPrizeLogoUrl(currentSector)
                : ''
            }
            resultType={currentSector ? getPrizeResultType(currentSector) : 'win'}
          />
        )}
      </div>
    </div>
  );
};

export default WheelOfFortune;
